import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FileIcon, Image, FileText, MoreVertical, Download, Pencil, Trash2, Copy, FolderIcon, Eye } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { RenameDialog } from './RenameDialog'
import { PreviewDialog } from './PreviewDialog'
import type { Asset, AssetItem } from '@/types/assets'

interface AssetListProps {
  items: AssetItem[]
  selectionMode?: boolean
  onAssetClick?: (asset: Asset) => void
  onFolderClick?: (folderId: number) => void
  onRenameAsset?: (assetId: number, name: string) => Promise<boolean>
  onDeleteAsset?: (assetId: number) => Promise<boolean>
  onRenameFolder?: (folderId: number, name: string) => Promise<boolean>
  onDeleteFolder?: (folderId: number) => Promise<boolean>
  onMove?: (assetId: number, folderId: number | null) => Promise<boolean>
  onCopyToProject?: (assetId: number, projectId: number, folderId?: number | null) => Promise<Asset | null>
  onCopyToOrganization?: (assetId: number, folderId?: number | null) => Promise<Asset | null>
  projects?: Array<{ id: number; name: string }>
}

function getFileIcon(contentType: string, size: 'sm' | 'md' = 'sm') {
  const sizeClass = size === 'sm' ? 'h-5 w-5' : 'h-8 w-8'
  if (contentType.startsWith('image/')) {
    return <Image className={`${sizeClass} text-green-500`} />
  }
  if (contentType === 'application/pdf') {
    return <FileText className={`${sizeClass} text-red-500`} />
  }
  if (contentType.includes('spreadsheet') || contentType === 'text/csv') {
    return <FileText className={`${sizeClass} text-emerald-500`} />
  }
  if (contentType.includes('document') || contentType === 'text/plain' || contentType === 'text/markdown') {
    return <FileText className={`${sizeClass} text-blue-500`} />
  }
  return <FileIcon className={`${sizeClass} text-gray-500`} />
}

function getFileType(contentType: string): string {
  if (contentType.startsWith('image/')) return 'Image'
  if (contentType === 'application/pdf') return 'PDF'
  if (contentType.includes('spreadsheet')) return 'Spreadsheet'
  if (contentType === 'text/csv') return 'CSV'
  if (contentType.includes('document')) return 'Document'
  if (contentType === 'text/plain') return 'Text'
  if (contentType === 'text/markdown') return 'Markdown'
  if (contentType === 'application/json') return 'JSON'
  return 'File'
}

export function AssetList({
  items,
  selectionMode = false,
  onAssetClick,
  onFolderClick,
  onRenameAsset,
  onDeleteAsset,
  onRenameFolder,
  onDeleteFolder,
  onMove,
  onCopyToProject,
  onCopyToOrganization,
  projects = []
}: AssetListProps) {
  const { t } = useTranslation('assets')
  const [selectedItem, setSelectedItem] = useState<AssetItem | null>(null)
  const [showRenameDialog, setShowRenameDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showPreviewDialog, setShowPreviewDialog] = useState(false)
  const [previewAsset, setPreviewAsset] = useState<Asset | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleRename = async (name: string) => {
    if (!selectedItem) return
    let success = false
    if (selectedItem.itemType === 'folder' && onRenameFolder) {
      success = await onRenameFolder(selectedItem.id, name)
    } else if (selectedItem.itemType === 'asset' && onRenameAsset) {
      success = await onRenameAsset(selectedItem.id, name)
    }
    if (success) {
      setShowRenameDialog(false)
      setSelectedItem(null)
    }
  }

  const handleDelete = async () => {
    if (!selectedItem) return
    setIsDeleting(true)
    let success = false
    if (selectedItem.itemType === 'folder' && onDeleteFolder) {
      success = await onDeleteFolder(selectedItem.id)
    } else if (selectedItem.itemType === 'asset' && onDeleteAsset) {
      success = await onDeleteAsset(selectedItem.id)
    }
    setIsDeleting(false)
    if (success) {
      setShowDeleteDialog(false)
      setSelectedItem(null)
    }
  }

  const handleDownload = (asset: Asset) => {
    const link = document.createElement('a')
    link.href = asset.file_url
    link.download = asset.name
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const renderFolderRow = (item: AssetItem & { itemType: 'folder' }) => (
    <div
      key={`folder-${item.id}`}
      className="grid grid-cols-[40px_1fr_80px_80px_40px] gap-2 px-3 py-2 items-center cursor-pointer hover:bg-accent"
      onClick={() => onFolderClick?.(item.id)}
    >
      <div className="flex items-center justify-center">
        <FolderIcon className="h-5 w-5 text-blue-500" />
      </div>
      <div className="font-medium truncate" title={item.name}>{item.name}</div>
      <div className="text-muted-foreground text-sm">{t('item', { count: item.assets_count + item.children_count })}</div>
      <div className="text-muted-foreground text-sm">{t('fileTypes.folder')}</div>
      <div>
        {!selectionMode && (onRenameFolder || onDeleteFolder) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={e => e.stopPropagation()}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onRenameFolder && (
                <DropdownMenuItem onClick={(e) => {
                  e.stopPropagation()
                  setSelectedItem(item)
                  setShowRenameDialog(true)
                }}>
                  <Pencil className="h-4 w-4 mr-2" />
                  {t('buttons.rename')}
                </DropdownMenuItem>
              )}
              {onDeleteFolder && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectedItem(item)
                      setShowDeleteDialog(true)
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t('buttons.delete')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  )

  const renderAssetRow = (item: AssetItem & { itemType: 'asset' }) => (
    <div
      key={`asset-${item.id}`}
      className={`grid grid-cols-[40px_1fr_80px_80px_40px] gap-2 px-3 py-2 items-center ${
        selectionMode ? 'cursor-pointer hover:bg-accent' : 'hover:bg-muted/30'
      }`}
      onClick={() => selectionMode && onAssetClick?.(item)}
    >
      <div className="flex items-center justify-center">
        {item.is_image && item.thumbnail_url ? (
          <img
            src={item.thumbnail_url}
            alt={item.name}
            className="w-8 h-8 object-cover rounded"
          />
        ) : (
          getFileIcon(item.content_type, 'sm')
        )}
      </div>
      <div className="font-medium truncate" title={item.name}>{item.name}</div>
      <div className="text-muted-foreground text-sm">{item.human_byte_size}</div>
      <div className="text-muted-foreground text-sm">{getFileType(item.content_type)}</div>
      <div>
        {!selectionMode && (onRenameAsset || onDeleteAsset || onMove || onCopyToProject || onCopyToOrganization) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={e => e.stopPropagation()}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => {
                setPreviewAsset(item)
                setShowPreviewDialog(true)
              }}>
                <Eye className="h-4 w-4 mr-2" />
                {t('buttons.preview')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDownload(item)}>
                <Download className="h-4 w-4 mr-2" />
                {t('buttons.download')}
              </DropdownMenuItem>

              {onRenameAsset && (
                <DropdownMenuItem onClick={() => {
                  setSelectedItem(item)
                  setShowRenameDialog(true)
                }}>
                  <Pencil className="h-4 w-4 mr-2" />
                  {t('buttons.rename')}
                </DropdownMenuItem>
              )}

              {onCopyToProject && projects.length > 0 && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Copy className="h-4 w-4 mr-2" />
                    {t('buttons.copyToProject')}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {projects.map(project => (
                      <DropdownMenuItem
                        key={project.id}
                        onClick={() => onCopyToProject(item.id, project.id)}
                      >
                        {project.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              )}

              {onCopyToOrganization && (
                <DropdownMenuItem onClick={() => onCopyToOrganization(item.id)}>
                  <Copy className="h-4 w-4 mr-2" />
                  {t('buttons.copyToOrganization')}
                </DropdownMenuItem>
              )}

              {onDeleteAsset && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={() => {
                      setSelectedItem(item)
                      setShowDeleteDialog(true)
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t('buttons.delete')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  )

  const deleteTitle = selectedItem?.itemType === 'folder'
    ? t('dialogs.deleteFolder.title')
    : t('dialogs.deleteFile.title')
  const deleteDescription = selectedItem?.itemType === 'folder'
    ? t('dialogs.deleteFolder.description', { name: selectedItem?.name })
    : t('dialogs.deleteFile.description', { name: selectedItem?.name })

  return (
    <>
      <div className="border rounded-lg overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-[40px_1fr_80px_80px_40px] gap-2 px-3 py-2 bg-muted/50 text-sm font-medium text-muted-foreground border-b">
          <div></div>
          <div>{t('table.name')}</div>
          <div>{t('table.size')}</div>
          <div>{t('table.type')}</div>
          <div></div>
        </div>

        {/* Rows */}
        <div className="divide-y">
          {items.map(item =>
            item.itemType === 'folder' ? renderFolderRow(item) : renderAssetRow(item)
          )}
        </div>
      </div>

      {/* Preview Dialog */}
      <PreviewDialog
        open={showPreviewDialog}
        onOpenChange={setShowPreviewDialog}
        asset={previewAsset}
      />

      {/* Rename Dialog */}
      <RenameDialog
        open={showRenameDialog}
        onOpenChange={setShowRenameDialog}
        currentName={selectedItem?.name || ''}
        onSubmit={handleRename}
        isFolder={selectedItem?.itemType === 'folder'}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{deleteTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('buttons.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? t('buttons.deleting') : t('buttons.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
